/**
 * Account → Branding (WO-INV1) — per-tenant invoice branding self-service.
 *
 * Owner-gated. The org picks ONE of three built-in invoice templates (classic | modern | minimal),
 * an accent color, whether to show its logo, and an optional footer note; it uploads/removes a
 * single logo image. These drive the branded invoice PDF (GET /api/invoices/:id/pdf) and the public
 * pay page. There are NO custom user-uploaded templates (out of scope by design).
 *
 * Every control performs a real backend round-trip via @beam/api-types (TanStack Query) with
 * loading / error states. No card data or keys cross the app. NEVER imports @smartbox/* (invariant 1).
 */
import { useEffect, useState } from 'react';
import { ActivityIndicator, Image, Pressable, StyleSheet, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InvoiceTemplate } from '@beam/api-types';
import { getBranding, updateBranding, uploadLogo, deleteLogo } from '../../../../src/api/branding';
import { pickLogoImage } from '../../../../src/platform/imagePicker';
import { Screen, Text, Card, Button, Field, Badge } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';

const TEMPLATES: Array<{ key: InvoiceTemplate; name: string; blurb: string }> = [
  { key: 'classic', name: 'Classic', blurb: 'Traditional, bordered, accountant-friendly' },
  { key: 'modern', name: 'Modern', blurb: 'Colored header band, card layout' },
  { key: 'minimal', name: 'Minimal', blurb: 'Whitespace, thin rules, single accent stroke' },
];

const ACCENT_PRESETS = ['#1f6feb', '#0f766e', '#b91c1c', '#7c3aed', '#c2410c', '#0d1117'];
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function BrandingScreen() {
  const queryClient = useQueryClient();
  const branding = useQuery({ queryKey: ['branding'], queryFn: getBranding });

  const [template, setTemplate] = useState<InvoiceTemplate>('classic');
  const [accent, setAccent] = useState('#1f6feb');
  const [footer, setFooter] = useState('');
  const [showLogo, setShowLogo] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  // Seed local form from server state once loaded.
  useEffect(() => {
    if (branding.data) {
      setTemplate(branding.data.branding.invoiceTemplate);
      setAccent(branding.data.branding.accentColor);
      setFooter(branding.data.branding.footerNote ?? '');
      setShowLogo(branding.data.branding.showLogo);
    }
  }, [branding.data]);

  const accentValid = HEX_RE.test(accent);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateBranding({
        invoiceTemplate: template,
        accentColor: accent.toLowerCase(),
        showLogo,
        footerNote: footer.trim() ? footer.trim() : null,
      }),
    onSuccess: (data) => {
      setSavedNote('Saved');
      queryClient.setQueryData(['branding'], data);
    },
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not save your branding.')),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const picked = await pickLogoImage();
      if (!picked) return null; // user canceled
      return uploadLogo({ contentBase64: picked.contentBase64, mimeType: picked.mimeType, fileName: picked.fileName });
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(['branding'], data);
    },
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not upload the logo.')),
  });

  const removeLogoMutation = useMutation({
    mutationFn: deleteLogo,
    onSuccess: (data) => queryClient.setQueryData(['branding'], data),
    onError: (e) => setFormError(extractErrorMessage(e, 'Could not remove the logo.')),
  });

  if (branding.isLoading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading branding" />
        </View>
      </Screen>
    );
  }

  if (branding.isError || !branding.data) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(branding.error, 'Failed to load branding settings.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => branding.refetch()} />
        </Card>
      </Screen>
    );
  }

  const data = branding.data;
  const dirtyAccent = accentValid ? accent : '#1f6feb';

  return (
    <Screen scroll>
      <Text variant="title">Branding</Text>
      <Text variant="body" color="secondary">
        Make your invoices look like your business. Pick a template, an accent color, and add your
        logo. These apply to every invoice you download or share, and to your public pay page.
      </Text>

      {/* --- Live preview --- */}
      <Card>
        <Text variant="label" color="secondary">Preview</Text>
        <View style={[styles.preview, { borderColor: dirtyAccent }]}>
          <View style={[styles.previewBar, { backgroundColor: dirtyAccent }]} />
          <View style={styles.previewBody}>
            {data.hasLogo && showLogo && data.logoUrl ? (
              <Image source={{ uri: data.logoUrl }} style={styles.previewLogo} resizeMode="contain" accessibilityLabel="Logo preview" />
            ) : (
              <Text variant="bodyStrong" style={{ color: dirtyAccent }}>YOUR BUSINESS</Text>
            )}
            <Text variant="small" color="secondary">INVOICE · {TEMPLATES.find((t) => t.key === template)?.name}</Text>
            <View style={[styles.previewLine, { width: '70%' }]} />
            <View style={[styles.previewLine, { width: '50%' }]} />
            <Text variant="data" style={[styles.previewTotal, { color: dirtyAccent }]}>Total Due  $4,210.78</Text>
          </View>
        </View>
      </Card>

      {/* --- Template picker --- */}
      <Card>
        <Text variant="label" color="secondary">Template</Text>
        {TEMPLATES.map((t) => {
          const selected = template === t.key;
          return (
            <Pressable
              key={t.key}
              onPress={() => {
                setTemplate(t.key);
                setSavedNote(null);
              }}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              accessibilityLabel={`${t.name} template`}
              style={[styles.templateRow, selected && { borderColor: dirtyAccent, backgroundColor: theme.color.bg.subtle }]}
            >
              <View style={styles.templateText}>
                <Text variant="bodyStrong">{t.name}</Text>
                <Text variant="small" color="secondary">{t.blurb}</Text>
              </View>
              {selected ? <Badge status="paid" label="Selected" /> : null}
            </Pressable>
          );
        })}
      </Card>

      {/* --- Accent color --- */}
      <Card>
        <Text variant="label" color="secondary">Accent color</Text>
        <View style={styles.swatches}>
          {ACCENT_PRESETS.map((c) => {
            const selected = accent.toLowerCase() === c.toLowerCase();
            return (
              <Pressable
                key={c}
                onPress={() => {
                  setAccent(c);
                  setSavedNote(null);
                }}
                accessibilityRole="button"
                accessibilityLabel={`Accent ${c}`}
                accessibilityState={{ selected }}
                style={[styles.swatch, { backgroundColor: c }, selected && styles.swatchSelected]}
              />
            );
          })}
        </View>
        <Field
          label="Custom hex"
          placeholder="#1f6feb"
          value={accent}
          onChangeText={(t) => {
            setAccent(t);
            setSavedNote(null);
          }}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Accent color hex"
        />
        {!accentValid ? (
          <Text variant="small" color="danger">Enter a 6-digit hex color like #1f6feb.</Text>
        ) : null}
      </Card>

      {/* --- Logo --- */}
      <Card>
        <Text variant="label" color="secondary">Logo</Text>
        {data.hasLogo && data.logoUrl ? (
          <>
            <Image source={{ uri: data.logoUrl }} style={styles.logo} resizeMode="contain" accessibilityLabel="Current logo" />
            <View style={styles.logoButtons}>
              <Button
                title="Replace"
                variant="secondary"
                size="sm"
                fullWidth={false}
                loading={uploadMutation.isPending}
                disabled={uploadMutation.isPending}
                onPress={() => {
                  setFormError(null);
                  uploadMutation.mutate();
                }}
                accessibilityLabel="Replace logo"
              />
              <Button
                title="Remove"
                variant="secondary"
                size="sm"
                fullWidth={false}
                loading={removeLogoMutation.isPending}
                disabled={removeLogoMutation.isPending}
                onPress={() => {
                  setFormError(null);
                  removeLogoMutation.mutate();
                }}
                accessibilityLabel="Remove logo"
              />
            </View>
          </>
        ) : (
          <>
            <Text variant="small" color="secondary">
              PNG or JPG works best. With no logo, your business name is used as a wordmark.
            </Text>
            <Button
              title="Upload logo"
              loading={uploadMutation.isPending}
              disabled={uploadMutation.isPending}
              onPress={() => {
                setFormError(null);
                uploadMutation.mutate();
              }}
              accessibilityLabel="Upload logo"
            />
          </>
        )}
        {data.hasLogo ? (
          <Pressable
            onPress={() => {
              setShowLogo((v) => !v);
              setSavedNote(null);
            }}
            accessibilityRole="switch"
            accessibilityState={{ checked: showLogo }}
            accessibilityLabel="Show logo on invoices"
            style={styles.toggleRow}
          >
            <Text variant="body">Show logo on invoices</Text>
            <Badge status={showLogo ? 'paid' : 'draft'} label={showLogo ? 'On' : 'Off'} />
          </Pressable>
        ) : null}
      </Card>

      {/* --- Footer note --- */}
      <Card>
        <Text variant="label" color="secondary">Footer note</Text>
        <Text variant="small" color="secondary">
          Optional. Shown at the bottom of every invoice (payment terms, thank-you, remit info).
        </Text>
        <Field
          placeholder="e.g. Net 14. Thank you for your business."
          value={footer}
          onChangeText={(t) => {
            setFooter(t);
            setSavedNote(null);
          }}
          multiline
          maxLength={280}
          accessibilityLabel="Footer note"
        />
        <Text variant="small" color="secondary">{footer.length}/280</Text>
      </Card>

      <Button
        title="Save branding"
        loading={saveMutation.isPending}
        disabled={saveMutation.isPending || !accentValid}
        onPress={() => {
          setFormError(null);
          saveMutation.mutate();
        }}
        accessibilityLabel="Save branding"
      />
      {savedNote ? <Text variant="small" color="secondary">{savedNote}</Text> : null}

      {formError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{formError}</Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
  preview: { borderWidth: 1, borderRadius: theme.radius.md, overflow: 'hidden', marginTop: theme.spacing.sm },
  previewBar: { height: 8, width: '100%' },
  previewBody: { padding: theme.spacing.md, gap: theme.spacing.xs },
  previewLogo: { height: 28, width: 120, alignSelf: 'flex-start' },
  previewLine: { height: 6, borderRadius: 3, backgroundColor: theme.color.border.default },
  previewTotal: { marginTop: theme.spacing.sm },
  templateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.color.border.default,
    borderRadius: theme.radius.md,
    marginTop: theme.spacing.sm,
  },
  templateText: { flex: 1, gap: theme.spacing.xs },
  swatches: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm, marginVertical: theme.spacing.sm },
  swatch: { width: 34, height: 34, borderRadius: 17, borderWidth: 2, borderColor: 'transparent' },
  swatchSelected: { borderColor: theme.color.text.primary },
  logo: { height: 64, width: 200, alignSelf: 'flex-start', marginVertical: theme.spacing.sm },
  logoButtons: { flexDirection: 'row', gap: theme.spacing.sm },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing.md,
  },
});
